/* eslint-disable @typescript-eslint/no-empty-function */
import { Icon } from '@iconify/react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Radio,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { createAssignment } from '../../services/assignment';
import { ISearchInvCustomerWithoutOrder, IVehicleComponentChange } from '../../types/inventory';
import { TextField, Center, Typography as CustomTypography } from '../../components';
import AssignmentModalControls from './AssignmentModalControls';
import SearchResults from './SearchResults';
import { searchCustomerWithOutOrder } from '../../services/inventory';
import { ICompSummaryBase } from '../../types';
import useSnackbarNotifications from '../../hooks/useSnackbarNotifications';
import { GLOBAL_USER } from '../../utils/constants';
import AssignmentActionModal from './AssignmentActionModal';
import useHeight from '../../hooks/useHeight';
import { usePermissions } from '../../hooks/usePermissions';

interface SearchFormProps {
  onSubmit: (q: string) => void;
}

const SearchForm: React.FC<SearchFormProps> = ({ onSubmit }) => {
  const [value, setValue] = useState('');

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(value);
  };

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        placeholder="Search by customer name"
        fullWidth
        endIcon={
          <IconButton size="small" onClick={() => onSubmit(value)}>
            <Icon icon="ant-design:search-outlined" />
          </IconButton>
        }
        onChange={(e) => {
          const val = e.target.value;
          if ((val && val.length > 2) || val === '') {
            onSubmit(val);
          }
          setValue(val);
        }}
      />
      <Box
        width="98.5%"
        sx={{
          marginTop: '10px',
          borderColor: '#456BD9',
          borderStyle: 'solid',
          borderWidth: '1px',
          background: '#fff',
          borderRadius: '4px',
          padding: '7px 10px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Icon icon="ri:information-line" fontSize={16} color="#456BD9" />
        <Typography fontSize="12px" paddingLeft="10px" color="#456BD9">
          Vehicle will be put on hold for customer. Assignment request will be placed when the order is confirmed
        </Typography>
      </Box>
    </form>
  );
};

interface SearchCustomerProps {
  onCustomerIdUpdate: (id: string) => void;
  onCustomerUpdate: (customerObj: any) => void;
}

const SearchCustomer: React.FC<SearchCustomerProps> = React.memo(({ onCustomerIdUpdate, onCustomerUpdate }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<ISearchInvCustomerWithoutOrder[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [loading, setLoading] = useState(false);
  const elementDOM = useRef(null);
  const [boxHeight, setBoxHeight] = useState('');
  const { height, offsetTop, offsetHeight } = useHeight(elementDOM, true, true);

  const handleSearch = async () => {
    if (!searchTerm) {
      return;
    }
    try {
      setLoading(true);
      const res = await searchCustomerWithOutOrder(searchTerm);
      setResults(res);
    } catch (error) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleSearch();
    if (!searchTerm) {
      onCustomerIdUpdate('');
      onCustomerUpdate({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const renderBody = () => {
    if (loading) {
      return (
        <Center>
          <CircularProgress />
        </Center>
      );
    }
    if (!searchTerm) {
      return (
        <Center>
          <Icon icon="ant-design:file-search-outlined" fontSize={28} color={theme.palette.grey[400]} />
          <Typography variant="body2" color={theme.palette.grey[400]}>
            You will see your search results here.
          </Typography>
        </Center>
      );
    }
    return !results.length ? (
      <Center>
        <Icon icon="healthicons:rdt-result-no-test" fontSize={28} color={theme.palette.grey[400]} />
        <Typography variant="body2" color={theme.palette.grey[400]}>
          Nothing to show
        </Typography>
      </Center>
    ) : (
      <Box sx={{ overflow: 'auto', height: `calc(90vh - ${boxHeight}px)` }}>
        <Stack spacing={1} width="99%">
          {results.map((result) => (
            <Box
              minHeight={50}
              key={result._id}
              display="flex"
              alignItems="center"
              gap={2}
              sx={{
                backgroundColor: '#fafafa',
                borderBottom: '1px solid #eeeeee',
              }}
            >
              <Radio
                onChange={(e) => {
                  setSelectedCustomer(result._id);
                  onCustomerIdUpdate(result._id);
                  onCustomerUpdate(result);
                }}
                checked={result._id === selectedCustomer}
                sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize: 18,
                  },
                }}
              />
              <Box>
                <Typography variant="body1">{`${result.firstName} ${result.lastName}`}</Typography>
                <Typography variant="caption" color={theme.palette.grey[800]}>
                  {result.phone || 'N/A'} | {result.email || 'N/A'}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };
  useEffect(() => {
    if (offsetTop && offsetHeight) {
      const tempboxHeight = `${offsetTop + offsetHeight}`;
      setBoxHeight(tempboxHeight);
    }
  }, [offsetTop, offsetHeight]);

  return (
    <>
      <Box ref={elementDOM}>
        <SearchForm
          onSubmit={(q) => {
            setSearchTerm(q);
            setSelectedCustomer('');
          }}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
        }}
      >
        {renderBody()}
      </Box>
    </>
  );
});

interface Props {
  newToggle: any;
  closeModal: () => void;
  toCompareVehicle: ICompSummaryBase & { [key: string]: string };
}

const AssignmentWithoutOrder: React.FC<Props> = ({ newToggle, closeModal, toCompareVehicle }) => {
  const theme = useTheme();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();
  const [comment, setComment] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [customer, setCustomer] = useState();
  const [creatingAssignment, setCreatingAssignment] = useState(false);
  const [page, setPage] = useState(0);
  const [nextDisable, setNextDisable] = useState(true);
  const [transactionErrors, setTransactionErrors] = useState(false);
  const [vehicleComponentChange, setVehicleComponentChange] = useState<IVehicleComponentChange>({
    wheelOptions: null,
    upgradeOptions: null,
  });
  const { hasPermissions, getRole } = usePermissions();
  const handleVehicleComponentChange = (type: string, updateComponent: IVehicleComponentChange) => {
    setVehicleComponentChange((prevState: IVehicleComponentChange): IVehicleComponentChange => {
      if (type === 'wheelOptions') {
        return {
          ...prevState,
          wheelOptions: updateComponent,
        };
      }
      if (type === 'upgradeOptions') {
        return {
          ...prevState,
          upgradeOptions: updateComponent,
        };
      }
      return {
        wheelOptions: null,
        upgradeOptions: null,
      };
    });
  };

  const handleCreateAssignment = async () => {
    try {
      setCreatingAssignment(true);
      // setCreatingAssignment(true);
      if (toCompareVehicle.batchNumber) {
        const data = {
          assignmentType: 'customer_without_order',
          comments: comment || '',
          requestor: GLOBAL_USER,
          customerId,
          invBatchNumber: toCompareVehicle.batchNumber,
          vehicleComponentChange,
          directApproval: false,
        };
        const createAssignmentResp = await createAssignment(data);

        if (createAssignmentResp.message === 'Success') {
          closeModal();
          showSuccessSnackbar({ message: 'The request has been successfully submitted.' });
        } else if (createAssignmentResp.message !== 'Success') {
          closeModal();
          showErrorSnackbar({ message: createAssignmentResp?.message });
        } else if (createAssignmentResp?.sfdcResponse && createAssignmentResp?.vmsResponse) {
          if (
            (createAssignmentResp?.sfdcResponse.statusCode !== 200 ||
              createAssignmentResp?.vmsResponse?.statusCode !== 200) &&
            createAssignmentResp.statusCode !== 200
          ) {
            setCreatingAssignment(false);
            setTransactionErrors(true);
          }
        }
      }
    } catch (error) {
      console.log(error);
      closeModal();
      showErrorSnackbar({ message: 'Error creating assignment' });
    } finally {
      closeModal();
      setCreatingAssignment(false);
    }
  };

  const updateSelectedCustomerId = useCallback((id: string) => {
    setCustomerId(id);
    setNextDisable(false);
  }, []);
  const updateSelectedCustomer = useCallback((customerObj: any) => {
    setCustomer(customerObj);
    setNextDisable(false);
  }, []);

  const handleBack = () => {
    newToggle(true);
    // eslint-disable-next-line
    page > 0 ? setPage(page - 1) : closeModal();
  };

  const handleNextScreen = () => {
    if (customerId) {
      setPage(page + 1);
      newToggle(false);
      setNextDisable(true);
    }
  };
  const conditionalComponent = () => {
    switch (page) {
      case 0:
        return (
          <SearchCustomer onCustomerIdUpdate={updateSelectedCustomerId} onCustomerUpdate={updateSelectedCustomer} />
        );
      case 1:
        return (
          <>
            <AssignmentActionModal
              componentChange={handleVehicleComponentChange}
              inventoryVehicle={toCompareVehicle}
              orderInfo={customer}
              transactionErrors={transactionErrors}
            />
            <Divider sx={{ my: 1 }} />
            <Box component="footer">
              {toCompareVehicle?.elegibleforAutoAssignment && !hasPermissions(['PERFORM_REQUEST_ONLY']) && (
                <Box
                  sx={{
                    marginTop: '10px',
                    borderColor: '#456BD9',
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    background: '#fff',
                    borderRadius: '4px',
                    padding: '7px 10px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Icon icon="ri:information-line" fontSize={14} color="#0365fc" />
                  {/* TODO: Need to check if we want this text on Customer Without Order section. */}
                  <CustomTypography weight={400} size={12} color="#1F1F1F" marginLeft={1}>
                    This request is eligible for automatic assignment.
                  </CustomTypography>
                </Box>
              )}
              <Typography variant="caption">Request Reason</Typography>
              <TextField
                required
                placeholder="Enter Reason (Required)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            </Box>
          </>
        );
      default:
        return (
          <SearchCustomer onCustomerIdUpdate={updateSelectedCustomerId} onCustomerUpdate={updateSelectedCustomer} />
        );
    }
  };
  return (
    <>
      <Stack my={2} flex={1} spacing={1} display="flex">
        {/* <Alert
          variant="outlined"
          severity="info"
          sx={{
            borderColor: '#5174db',
            color: '#5174db',
            padding: theme.spacing(0.1, 0.7),
            ' svg': {
              color: '#5174db',
            },
          }}
        >
          Vehicle will be put on hold for customer. Assignment request will be placed when the order is confirmed
        </Alert> */}
        {conditionalComponent()}
      </Stack>
      <AssignmentModalControls
        onBack={handleBack}
        onSubmit={handleCreateAssignment}
        visibilityFlag={page > 0}
        submitDisabled={!customerId || creatingAssignment || comment === ''}
        nextEnable={nextDisable || !customerId}
        onNext={handleNextScreen}
        orderAvailability={false}
        autoAssignmentElidgible={false}
      />
    </>
  );
};

export default AssignmentWithoutOrder;
